import {
  HttpBackend,
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Observable,
  Subject,
  catchError,
  combineLatest,
  filter,
  forkJoin,
  from,
  map,
  mergeMap,
  of,
  scan,
  startWith,
  switchMap,
  tap,
  toArray,
} from 'rxjs';
import { IFileProgressEvent } from '../../../shared/services/filestore.service';
import {
  IDataCaptureSegment,
  IDataCaptureSheet,
  IDataCaptureSheetBase,
  IDataCaptureSummariesResponse,
  IExternalConnectionUpload,
  IRespondent,
  IRespondentAnswer,
  IRespondentWithAnswers,
  ISuggestedQuestionCountResponse,
  RespondentAnswers,
  RespondentSource,
  SuggestionStatus,
} from '../models/data-capture.models';
import { DiscussionGuideUploadJob } from '../models/discussion-guide-upload-job.models';

export interface IUploadExternalConnectionFilesPayload {
  externalRespondentId: string;
  opportunitySegmentId: string;
  audioExtension?: string;
}

export interface IPutFile {
  fileKey: string;
  uploadUrl: string;
}

export interface IRespondentAnswerUpdate extends Partial<IRespondentAnswer> {
  respondent_id: string;
  question_id: string;
  answer_format: IDataCaptureSheet['answer_format'];
  answer_method: 'manual';
}

export interface IRegenerateAnswerPayload {
  respondentId: string;
  questionId: string;
  type: RespondentSource;
}

export interface IDiscussionGuideMessage {
  discussionGuideId: string;
  discussionGuideKey: string;
  sheetId: string;
}

@Injectable({
  providedIn: 'root',
})
export class DataCaptureService {
  private readonly baseUrl = '/data-capture';
  private readonly fanOutChunkSize = 10;

  private httpNoAuth = new HttpClient(this.httpBackend);

  private sheets$ = new Subject<IDataCaptureSheet[]>();
  private sheetsInner$ = this.sheets$.pipe(
    scan<IDataCaptureSheet[], IDataCaptureSheet[]>(
      (prev, curr) => [...prev, ...curr],
      []
    ),
    startWith([])
  );

  private deletedSheet$ = new Subject<string>();
  private deletedSheetIds$ = this.deletedSheet$.pipe(
    scan<string, string[]>((prev, curr) => [...prev, curr], []),
    startWith([])
  );

  allSheets$ = combineLatest([this.sheetsInner$, this.deletedSheetIds$]).pipe(
    map(([sheets, deletedSheetIds]) =>
      this.joinSheets(sheets, deletedSheetIds)
    ),
    startWith<Record<string, IDataCaptureSheet[]>>({})
  );

  constructor(
    private http: HttpClient,
    private httpBackend: HttpBackend
  ) {}

  querySheetsByOpportunityId(opportunityId: string) {
    return this.http
      .get<IDataCaptureSheet[]>(
        `${this.baseUrl}/sheets?opportunityId=${opportunityId}`
      )
      .pipe(
        tap((sheets) => {
          this.sheets$.next(sheets);
        })
      );
  }

  getSheet(dataCaptureSheetId: string) {
    return this.http
      .get<IDataCaptureSheet>(`${this.baseUrl}/sheets/${dataCaptureSheetId}`)
      .pipe(
        tap((sheet) => {
          this.sheets$.next([sheet]);
        })
      );
  }

  createSheet(
    payload: Omit<IDataCaptureSheetBase, 'data_capture_sheet_id'> & {
      data_capture_sheet_id?: string;
    }
  ) {
    return this.http
      .post<IDataCaptureSheet>(`${this.baseUrl}/sheets`, payload)
      .pipe(
        tap((sheet) => {
          this.sheets$.next([sheet]);
        })
      );
  }

  editSheet(
    payload: Partial<IDataCaptureSheet> & { data_capture_sheet_id: string } & {
      rebuild_question_ids: string[];
    }
  ) {
    return this.http.patch<IDataCaptureSheet>(
      `${this.baseUrl}/sheets/${payload.data_capture_sheet_id}`,
      payload
    );
  }

  deleteSheet(dataCaptureSheetId: string) {
    return this.http
      .delete<undefined>(`${this.baseUrl}/sheets/${dataCaptureSheetId}`)
      .pipe(
        tap(() => {
          this.deletedSheet$.next(dataCaptureSheetId);
        })
      );
  }

  getRespondentsByDataCaptureSheetId(dataCaptureSheetId: string) {
    return this.http.get<IRespondent[]>(
      `${this.baseUrl}/sheets/${dataCaptureSheetId}/respondents`
    );
  }

  getRespondentAnswers(dataCaptureSheetId: string, respondentId: string) {
    return this.http.get<RespondentAnswers>(
      `${this.baseUrl}/sheets/${dataCaptureSheetId}/respondents/${respondentId}/answers`
    );
  }

  getRespondentsWithAnswersByDataCaptureSheetId(
    dataCaptureSheetId: string
  ): Observable<IRespondentWithAnswers[]> {
    return this.getRespondentsByDataCaptureSheetId(dataCaptureSheetId).pipe(
      switchMap((respondents) => {
        if (!respondents.length) {
          return of([]);
        }

        return from(
          respondents.map((respondent, index) => ({ respondent, index }))
        ).pipe(
          mergeMap(
            ({ respondent, index }) =>
              this.getRespondentAnswers(
                dataCaptureSheetId,
                respondent.respondentId
              ).pipe(
                map((answers) => ({
                  index,
                  respondent: { ...respondent, answers },
                }))
              ),
            this.fanOutChunkSize
          ),
          toArray(),
          map((results) =>
            results
              .sort((a, b) => a.index - b.index)
              .map(({ respondent }) => respondent)
          )
        );
      })
    );
  }

  getSummariesByDataCaptureSheetId(
    dataCaptureSheetId: string,
    questionIds: string[]
  ) {
    const chunks = this.chunk([...new Set(questionIds)], this.fanOutChunkSize);

    if (!chunks.length) {
      return of({});
    }

    return forkJoin(
      chunks.map((ids) =>
        this.http.get<IDataCaptureSummariesResponse>(
          `${this.baseUrl}/sheets/${dataCaptureSheetId}/summaries?questionIds=${ids
            .map((id) => encodeURIComponent(id))
            .join(',')}`
        )
      )
    ).pipe(
      map((chunked) =>
        chunked.reduce<IDataCaptureSummariesResponse>(
          (acc, part) => ({ ...acc, ...part }),
          {}
        )
      )
    );
  }

  getAvailableSegments(opportunityId: string) {
    return this.http
      .get<IDataCaptureSegment[]>(
        `${this.baseUrl}/available-segments?opportunityId=${opportunityId}`
      )
      .pipe(
        map((segments) =>
          segments.sort((a, b) => {
            if (a.occupied && !b.occupied) {
              return 1;
            }

            if (!a.occupied && b.occupied) {
              return -1;
            }

            return a.segmentName.localeCompare(b.segmentName);
          })
        )
      );
  }

  updateRespondentAnswer(respondentAnswer: IRespondentAnswerUpdate[]) {
    return this.http.patch<IRespondentAnswer[]>(
      `${this.baseUrl}/respondent-answer`,
      respondentAnswer
    );
  }

  regenerateRespondentAnswer(payload: IRegenerateAnswerPayload) {
    return this.http.post<null>(
      `${this.baseUrl}/respondent-answer/regenerate`,
      payload
    );
  }

  uploadExternalConnectionFile(payload: IUploadExternalConnectionFilesPayload) {
    const params = new URLSearchParams({
      externalRespondentId: payload.externalRespondentId,
      opportunitySegmentId: payload.opportunitySegmentId,
      ...(payload.audioExtension
        ? { audioExtension: payload.audioExtension }
        : {}),
    });

    return this.http.get<{
      audio: IPutFile;
      transcript: IPutFile;
    }>(`${this.baseUrl}/external-connections/upload?${params.toString()}`);
  }

  createExternalConnection(payload: IExternalConnectionUpload) {
    return this.http.put<undefined>(
      `${this.baseUrl}/external-connections/create`,
      payload
    );
  }

  deleteExternalConnection(externalConnectionId: string) {
    return this.http.delete<undefined>(
      `${this.baseUrl}/external-connections/${externalConnectionId}`
    );
  }

  uploadDiscussionGuide(dataCaptureSheetId: string) {
    return this.http.get<IPutFile>(
      `${this.baseUrl}/sheets/discussion-guide/upload?dataCaptureSheetId=${dataCaptureSheetId}`
    );
  }

  createDiscussionGuide(message: IDiscussionGuideMessage) {
    return this.http.post<void>(
      `${this.baseUrl}/sheets/discussion-guide/upload`,
      message
    );
  }

  pollDiscussionGuideStatus(discussionGuideId: string) {
    return this.http.get<DiscussionGuideUploadJob | null>(
      `${this.baseUrl}/sheets/discussion-guide/status?discussionGuideId=${discussionGuideId}`
    );
  }

  uploadFile(file: File, uploadUrl: string): Observable<IFileProgressEvent> {
    const req = new HttpRequest('PUT', uploadUrl, file, {
      reportProgress: true,
    });
    return this.httpNoAuth.request(req).pipe(
      map((event) => this.getProgressEvent(event, file.name)),
      filter((event) => event.status === 'complete'),
      catchError(() => this.handleError(file.name))
    );
  }

  updateSuggestionStatus(
    dataCaptureSheetId: string,
    questionId: string,
    status: SuggestionStatus
  ) {
    return this.http
      .patch<IDataCaptureSheet>(
        `${this.baseUrl}/sheets/${dataCaptureSheetId}/suggested-questions/${questionId}/status`,
        { status }
      )
      .pipe(
        tap((sheet) => {
          this.sheets$.next([sheet]);
        })
      );
  }

  getSuggestedQuestionCount(dataCaptureSheetId: string, threshold: number) {
    return this.http.get<ISuggestedQuestionCountResponse>(
      `${this.baseUrl}/sheets/${dataCaptureSheetId}/suggested-questions/count?threshold=${threshold}`
    );
  }

  setSheets(sheets: IDataCaptureSheet[]) {
    this.sheets$.next(sheets);
  }

  private getProgressEvent(
    event: HttpEvent<unknown>,
    fileName: string
  ): IFileProgressEvent {
    switch (event.type) {
      case HttpEventType.Sent:
        return {
          fileName,
          percentageComplete: 0,
          status: 'started',
        };

      case HttpEventType.DownloadProgress:
      case HttpEventType.UploadProgress:
        return {
          fileName,
          percentageComplete: Math.round((100 * event.loaded) / event.total),
          status: 'ongoing',
        };

      case HttpEventType.Response:
        return {
          fileName,
          percentageComplete: 100,
          status: 'complete',
        };
    }
  }

  private handleError(fileName: string): Observable<IFileProgressEvent> {
    return of({
      fileName,
      percentageComplete: 0,
      status: 'failed',
    });
  }

  private joinSheets(
    dataCaptureSheets: IDataCaptureSheet[],
    deletedDataCaptureSeetIds: string[]
  ) {
    const sheetMap = dataCaptureSheets.reduce<
      Record<string, IDataCaptureSheet>
    >((prev, curr) => ({ ...prev, [curr.data_capture_sheet_id]: curr }), {});

    return Object.values(sheetMap)
      .filter(
        (q) => !deletedDataCaptureSeetIds.includes(q.data_capture_sheet_id)
      )
      .reduce<Record<string, IDataCaptureSheet[]>>(
        (prev, curr) => ({
          ...prev,
          [curr.opportunity_id]: [...(prev[curr.opportunity_id] || []), curr],
        }),
        {}
      );
  }

  private chunk<T>(items: T[], size: number): T[][] {
    const chunks: T[][] = [];

    for (let index = 0; index < items.length; index += size) {
      chunks.push(items.slice(index, index + size));
    }

    return chunks;
  }
}
